import React, { ComponentType, forwardRef, Ref } from 'react';

import { useFocusManager } from 'hooks/focus';

type FocusManagerType = ReturnType<typeof useFocusManager>;

export interface WithFocusManagerProps {
  focusManager: FocusManagerType;
}

interface HOCProps {
  forwardedRef?: Ref<unknown>;
}

function FocusManagerHOC<P extends WithFocusManagerProps>(
  Component: ComponentType<P>,
) {
  const FocusManagerWrapper = ({
    forwardedRef = null,
    ...props
  }: HOCProps & Omit<P, keyof WithFocusManagerProps>) => {
    const focusManager = useFocusManager();
    return (
      <Component {...(props as P)} focusManager={focusManager} ref={forwardedRef} />
    );
  };

  FocusManagerWrapper.displayName = `FocusManagerHOC(${
    Component.displayName || Component.name || 'Component'
  })`;

  const ForwardedComponent = forwardRef<
    unknown,
    Omit<P, keyof WithFocusManagerProps> & Omit<HOCProps, 'forwardedRef'>
  >((props, ref) => <FocusManagerWrapper {...props} forwardedRef={ref} />);

  ForwardedComponent.displayName = FocusManagerWrapper.displayName;

  return ForwardedComponent;
}

export default FocusManagerHOC;
